import { AxiosResponse } from "axios";
import React from "react";
import { ScrollView, StyleSheet, TextProps, View, ViewProps } from "react-native";
import { useSelector } from "react-redux";
import { Radio } from "src/components";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdIconButton } from "src/components/forms/icon-button";
import { PdInputNumber, PdInputPercentage } from "src/components/forms/input";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { LogoSmall, PdArrowLeft, PdArrowRight, PdShopify } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import { PdModal, PdModalCloseButton } from "src/components/overlay";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { tModalComponentProps } from "src/redux/modals/types";
import apiNew from "src/util/apiNew";
import { formatMoney } from "src/util/gFormatting";
import { tShopifyProductsImportRequestBody } from "tmt_api/src/types/types";
import { useQuery } from "../api";
import { useIsMobile } from "../ui";

// eslint-disable-next-line no-shadow
enum eStep {
  ProductsSelect = 0,
  ProductsPrice = 1,
  ProductsPriceCalculation = 2,
  ProductsCaseSelect = 3,
  ProductsCase = 4,
  ProductsCategory = 5,
  Confirmation = 6,
}

type tValues = {
  type: tPdSelectOption;
  priceOptionIndex: number;
  caseOptionIndex: number;
  case: number | null;
  category: tPdSelectOption;
  retailPriceMultiplier: number | null;
  wholesalePricePercent: number | null;
};

interface iImportProductsContext {
  values: tValues;
  productTags: string[];
  productTagsIsLoading: boolean;
  productCategories: tPdSelectOption[];
  isProcessing: boolean;
  updateValues: (newValues: Partial<tValues>) => void;
  onNavigate: (step: number) => void;
  onClose: tModalComponentProps["onClose"];
  onResolve: tModalComponentProps["onResolve"];
  setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
}

const wholesaleExamplePrice = 50;
const retailExamplePrice = 100;

const getShopifyImportationDetails = () =>
  apiNew.get<{ productTags: string[] }>("/shopify/importation-details").then((res) => res.data);

const postShopifyProductsImport = (payload: tShopifyProductsImportRequestBody) =>
  apiNew
    .post<tShopifyProductsImportRequestBody, AxiosResponse<void>>("shopify/products/import", payload)
    .then((res) => res.data);

const ImportProductsContext = React.createContext<Partial<iImportProductsContext>>({});

const useImportProductsContext = () => React.useContext(ImportProductsContext);

const ShopifyImportHeader: React.FC<ViewProps> = ({ children, ...rest }) => (
  <View style={[styles.alignCenter, styles.mb4, styles.widthFull]} {...rest}>
    <View style={[styles.flexRow, styles.alignCenter, styles.justifyCenter, styles.widthFull, styles.mb4]}>
      <PdShopify style={styles.heightMd} />
      <PdArrowRight style={[styles.heightXxs, styles.mx2]} />
      <LogoSmall style={styles.heightMd} />
    </View>
    {children}
  </View>
);

const ShopifyImportHeaderTitle: React.FC<TextProps> = (props) => (
  <PdText style={[styles.textBold, styles.textCenter, styles.mb2]} {...props} />
);

const ShopifyImportHeaderDescription: React.FC<TextProps> = (props) => (
  <PdText style={[styles.textSm, styles.textCenter]} {...props} />
);

const ProductsSelect: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, productTags, productTagsIsLoading, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(eStep.ProductsPrice);
  };

  const handleOnChange = (option: tPdSelectOption | null) => {
    if (option) updateValues?.({ type: option });
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>Select Products to import</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          You can add all products or choose a specific set of products based on your Shopify tags.
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <View style={[styles.widthFull, styles.zIndex1, styles.mb20]}>
        <PdSelect
          value={values?.type}
          options={[
            { value: "all-products", label: "All Products" },
            ...(productTags?.map((option) => ({ value: option, label: option })) ?? []),
          ]}
          isClearable={false}
          isLoading={productTagsIsLoading}
          onChange={handleOnChange}
        />
        <PdText style={styles.textXs}>Your Shopify products tags will appear in the dropdown</PdText>
      </View>

      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ProductsPrice: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(eStep.ProductsPriceCalculation);
  };

  const handleOnSelect = ({ myIndex }: { myValue: string; myIndex: number }) => {
    updateValues?.({ priceOptionIndex: myIndex });
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>How do you price your products on Shopify</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          We&apos;ll use this information to set your wholesale and recommended retail pricing on The Main Tab. You can
          adjust pricing at any time.
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <View style={styles.mb20}>
        <Radio
          selectedIndex={values?.priceOptionIndex ?? 0}
          myOptions={["I use retail pricing on Shopify", "I use wholesale pricing on Shopify"]}
          myFlow="column"
          labelTextStyle={styles.radioLabel}
          onSelect={handleOnSelect}
        />
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ProductsPriceCalculation: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(eStep.ProductsCaseSelect);
  };

  const handleOnChangeRetailPrice = (value: number | null) => {
    updateValues?.({ retailPriceMultiplier: value });
  };

  const handleOnChangeWholesalePrice = (value: number | null) => {
    updateValues?.({ wholesalePricePercent: value });
  };

  if (values?.priceOptionIndex === 0) {
    return (
      <>
        <ShopifyImportHeader>
          <ShopifyImportHeaderTitle>How should we calculate your retail prices?</ShopifyImportHeaderTitle>
          <ShopifyImportHeaderDescription>
            We&apos;ll automatically set your retail prices using the calculation below. You can update your prices as
            any time.
          </ShopifyImportHeaderDescription>
        </ShopifyImportHeader>
        <View style={[styles.flexRow, styles.alignCenter, styles.widthFull, styles.flex1, styles.mb20]}>
          <View style={[styles.alignCenter, styles.bgPink, styles.widthSmall, styles.py2]}>
            <PdText style={styles.textSm}>Wholesale</PdText>
            <PdText style={styles.textSm}>{formatMoney(wholesaleExamplePrice)}</PdText>
          </View>
          <PdText style={[styles.textLg, styles.mx3]}>X</PdText>
          <View style={styles.flex1}>
            <PdInputNumber value={values?.retailPriceMultiplier ?? 0} onChangeNumber={handleOnChangeRetailPrice} />
          </View>
          <PdText style={[styles.textLg, styles.mx3]}>=</PdText>
          <View style={[styles.alignCenter, styles.bgPink, styles.widthSmall, styles.py2]}>
            <PdText style={styles.textSm}>Retail</PdText>
            <PdText style={styles.textSm}>
              {formatMoney(wholesaleExamplePrice * (values?.retailPriceMultiplier ?? 0))}
            </PdText>
          </View>
        </View>
        <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
          <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton style={styles.flex1} onPress={handleOnNavigate}>
            <PdButtonLabel>Continue</PdButtonLabel>
          </PdButton>
        </PdStack>
      </>
    );
  }

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>How should we calculate your wholesale prices?</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          We&apos;ll automatically set your wholesale prices using the calculation below. You can update your prices as
          any time.
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <View style={[styles.flexRow, styles.alignCenter, styles.widthFull, styles.flex1, styles.mb20]}>
        <View style={[styles.alignCenter, styles.bgPink, styles.widthSmall, styles.py2]}>
          <PdText style={styles.textSm}>Retail</PdText>
          <PdText style={styles.textSm}>{formatMoney(retailExamplePrice)}</PdText>
        </View>
        <PdText style={[styles.textLg, styles.mx3]}>X</PdText>

        <View style={[styles.pdInputGroup, styles.flex1]}>
          <PdInputPercentage
            style={[styles.widthFull, styles.border0]}
            value={values?.wholesalePricePercent ?? 0}
            onChangeNumber={handleOnChangeWholesalePrice}
          />
          <PdText style={styles.mr2}>%</PdText>
        </View>

        <PdText style={[styles.textLg, styles.mx3]}>=</PdText>
        <View style={[styles.alignCenter, styles.bgPink, styles.widthSmall, styles.py2]}>
          <PdText style={styles.textSm}>Wholesale</PdText>
          <PdText style={styles.textSm}>
            {formatMoney(retailExamplePrice * ((values?.wholesalePricePercent ?? 0) / 100))}
          </PdText>
        </View>
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ProductsCaseSelect: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(values?.caseOptionIndex === 0 ? eStep.ProductsCase : eStep.ProductsCategory);
  };

  const handleOnSelect = ({ myIndex }: { myValue: string; myIndex: number }) => {
    updateValues?.({ caseOptionIndex: myIndex });
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>Will you sell your products in cases?</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          Brands sometimes sell products bundled in a case
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <View style={styles.mb20}>
        <Radio
          selectedIndex={values?.caseOptionIndex}
          myOptions={["Yes", "No"]}
          myFlow="column"
          labelTextStyle={styles.radioLabel}
          onSelect={handleOnSelect}
        />
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ProductsCase: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(eStep.ProductsCategory);
  };

  const handleOnChangeNumber = (value: number | null) => {
    updateValues?.({ case: value });
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>How many products are in a case?</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          If your products are sold in different case sizes, tell us what the most common case size is.
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <View style={[styles.widthFull, styles.mb20]}>
        <PdFormLabel>Number of products in a case</PdFormLabel>
        <View style={[styles.pdInputGroup, styles.flex1]}>
          <PdInputNumber
            style={[styles.widthFull, styles.border0]}
            value={values?.case ?? 0}
            onChangeNumber={handleOnChangeNumber}
          />
          <PdText style={styles.mr2}>units</PdText>
        </View>

        <PdText style={styles.textXs}>A &quot;unit&quot; is the smallest resellable format of your product</PdText>
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ProductsCategory: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, productCategories, updateValues, onNavigate, onClose } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnNavigate = () => {
    onNavigate?.(eStep.Confirmation);
  };

  const handleOnChange = (option: tPdSelectOption | null) => {
    if (option) updateValues?.({ category: option });
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>Are these products all the same type?</ShopifyImportHeaderTitle>
        <ShopifyImportHeaderDescription>
          We can use this information to automatically add a product type during import.
        </ShopifyImportHeaderDescription>
      </ShopifyImportHeader>
      <PdText style={styles.mb4}>
        Please select one category that best matches your brand. Once import is complete, you can then edit categories
        per SKU.
      </PdText>
      <View style={[styles.widthFull, styles.zIndex1, styles.mb20]}>
        <PdSelect
          containerStyle={[]}
          value={values?.category}
          options={productCategories ?? []}
          isClearable={false}
          onChange={handleOnChange}
        />
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton colorScheme="purple" style={styles.flex1} variant="outline" onPress={handleOnClose}>
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnNavigate}>
          <PdButtonLabel>Continue</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ReviewImportDetails: React.FC = () => {
  const isMobile = useIsMobile();
  const { values, isProcessing, setProcessing, onClose, onResolve } = useImportProductsContext();

  const handleOnClose = () => {
    onClose?.();
  };

  const handleOnImport = async () => {
    try {
      setProcessing?.(true);
      await postShopifyProductsImport({
        tag: values?.type.value === "all-products" ? undefined : values?.type.value,
        retailPriceMultiplier:
          values?.priceOptionIndex === 0 && values?.retailPriceMultiplier ? values?.retailPriceMultiplier : undefined,
        wholesalePricePercent:
          values?.priceOptionIndex === 1 && values?.wholesalePricePercent ? values?.wholesalePricePercent : undefined,
        caseSize: values?.caseOptionIndex === 0 && values.case ? values.case : 1,
        category: values?.category?.value ?? undefined,
      });
      onResolve?.();
    } catch (er) {
      setProcessing?.(false);
    }
  };

  return (
    <>
      <ShopifyImportHeader>
        <ShopifyImportHeaderTitle>Review your import details</ShopifyImportHeaderTitle>
      </ShopifyImportHeader>
      <View style={styles.mb20}>
        <View style={[styles.bgPink, styles.widthFull, styles.px3, styles.py2, styles.mb4]}>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={[styles.flex1, styles.textSm, styles.textBold]}>Selected Products</PdText>
            <PdText style={[styles.flex1, styles.textSm]}>{values?.type.label ?? "-"}</PdText>
          </View>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={[styles.flex1, styles.textSm, styles.textBold]}>Case Size</PdText>
            <PdText style={[styles.flex1, styles.textSm]}>{values?.caseOptionIndex === 0 ? values.case : "No"}</PdText>
          </View>
          <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
            <PdText style={[styles.flex1, styles.textSm, styles.textBold]}>Category</PdText>
            <PdText style={[styles.flex1, styles.textSm]}>{values?.category.label ?? "-"}</PdText>
          </View>
          <View style={[styles.flexRow, styles.alignCenter]}>
            <PdText style={[styles.flex1, styles.textSm, styles.textBold]}>Est Import Time</PdText>
            <PdText style={[styles.flex1, styles.textSm]}>5 - 15 minutes</PdText>
          </View>
        </View>
        <PdText style={styles.textSm}>
          After the import is complete, please review each product to confirm or update the product type, case quantity,
          and pricing details.
        </PdText>
      </View>
      <PdStack style={styles.widthFull} direction={isMobile ? "vertical" : "horizontal"} isReversed={isMobile}>
        <PdButton
          colorScheme="purple"
          style={styles.flex1}
          variant="outline"
          onPress={handleOnClose}
          isDisabled={isProcessing}
        >
          <PdButtonLabel>Cancel</PdButtonLabel>
        </PdButton>
        <PdButton style={styles.flex1} onPress={handleOnImport} isLoading={isProcessing}>
          <PdButtonLabel>Start Import</PdButtonLabel>
        </PdButton>
      </PdStack>
    </>
  );
};

const ShopifyImportProductsModal: React.FC<tModalComponentProps> = ({ onClose, onResolve }) => {
  const categories = useSelector((state) => state.ConfigsReducer.data.shop?.productCategories ?? {});
  const productCategories = React.useMemo(() => {
    const _categories: tPdSelectOption[] = [];
    for (const level0 of Object.keys(categories)) {
      const level1 = categories[level0];
      for (const lvl1 of level1) {
        const value = `${level0} - ${lvl1}`;
        _categories.push({ value, label: value });
      }
    }
    return _categories.sort((category1, category2) => category1.label.localeCompare(category2.label));
  }, [categories]);
  const [step, setStep] = React.useState(eStep.ProductsSelect);
  const [isProcessing, setProcessing] = React.useState(false);
  const productTagsQuery = useQuery(getShopifyImportationDetails);
  const [values, setValues] = React.useState<tValues>({
    type: { value: "all-products", label: "All Products" },
    priceOptionIndex: 1,
    caseOptionIndex: 0,
    case: 2,
    category: productCategories[0],
    retailPriceMultiplier: 2,
    wholesalePricePercent: 50,
  });

  const handleOnClose = () => {
    onClose();
  };

  const handleOnGoBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleOnNavigate = async (nextStep: number) => {
    setStep(nextStep);
  };

  const updateValues = (newValues: Partial<tValues>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
  };

  return (
    <PdModal style={styles.modal}>
      {step > 0 && (
        <PdIconButton
          isDisabled={isProcessing}
          onPress={handleOnGoBack}
          variant="ghost"
          icon={<PdArrowLeft style={styles.arrowLeftIcon} />}
          style={styles.modalBackButton}
        />
      )}
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <ImportProductsContext.Provider
          value={{
            values,
            updateValues,
            productTags: productTagsQuery.data?.productTags,
            productTagsIsLoading: productTagsQuery.isLoading,
            productCategories,
            isProcessing,
            onNavigate: handleOnNavigate,
            onClose,
            onResolve,
            setProcessing,
          }}
        >
          {step === eStep.ProductsSelect && <ProductsSelect />}
          {step === eStep.ProductsPrice && <ProductsPrice />}
          {step === eStep.ProductsPriceCalculation && <ProductsPriceCalculation />}
          {step === eStep.ProductsCaseSelect && <ProductsCaseSelect />}
          {step === eStep.ProductsCase && <ProductsCase />}
          {step === eStep.ProductsCategory && <ProductsCategory />}
          {step === eStep.Confirmation && <ReviewImportDetails />}
        </ImportProductsContext.Provider>
      </ScrollView>
    </PdModal>
  );
};

const useShopifyImportProductsModal = (): tUseModal => {
  return useModal(ShopifyImportProductsModal);
};

export default useShopifyImportProductsModal;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  arrowLeftIcon: {
    width: 22,
  },
  bgPink: {
    backgroundColor: kColors.brandPink2,
  },
  border0: {
    borderWidth: 0,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  heightMd: {
    height: 30,
  },
  heightXxs: {
    height: 7,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb20: {
    marginBottom: 20 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 450,
    width: "100%",
  },
  modalBackButton: {
    left: 0,
    margin: 4 * kStyleConst.kSpacing,
    position: "absolute",
    top: 0,
    zIndex: 10,
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mx2: {
    marginHorizontal: 2 * kStyleConst.kSpacing,
  },
  mx3: {
    marginHorizontal: 3 * kStyleConst.kSpacing,
  },

  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  px3: {
    paddingHorizontal: 3 * kStyleConst.kSpacing,
  },
  py2: {
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  radioLabel: {
    fontFamily: "Poppins",
    fontSize: 10,
    fontWeight: "300",
    letterSpacing: 0.8,
  },
  textBold: {
    fontWeight: "500",
  },
  textCenter: {
    textAlign: "center",
  },
  textLg: {
    fontSize: 22,
  },
  textSm: {
    fontSize: 12,
  },
  textXs: {
    fontSize: 10,
  },
  widthFull: {
    width: "100%",
  },
  widthSmall: {
    width: 100,
  },
  zIndex1: {
    zIndex: 1,
  },
});
